<template>
  <component :is="tag" ref="_ref" v-bind="_props" :class="buttonKls" :style="buttonStyle" @click="handleClick">
    <template v-if="loading">
      <slot v-if="$slots.loading" name="loading" />
      <z-icon v-else :class="ns.is('loading')">
        <component :is="resolvedLoadingIcon" />
      </z-icon>
    </template>
      <z-icon v-else-if="icon || $slots.icon">
        <component :is="resolvedIcon" v-if="icon" />
        <slot v-else name="icon" />
      </z-icon>
    <span v-if="$slots.default" :class="{ [ns.em('text', 'expand')]: shouldAddSpace }">
      <slot />
    </span>
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { ZIcon } from '@zoryn/components/icon'
import { Loading } from '@zoryn/icons'
import { useNamespace } from '@zoryn/hooks'
import { resolveIconComponent } from '@zoryn/utils'
import { useButton } from './use-button'
import { buttonEmits } from './button'
import { useButtonCustomStyle } from './button-custom'

import type { ButtonProps } from './button'

defineOptions({
  name: 'ZButton',
})

const props = withDefaults(defineProps<ButtonProps>(), {
  disabled: undefined,
  type: '',
  nativeType: 'button',
  loadingIcon: () => resolveIconComponent(Loading)!,
  plain: undefined,
  text: undefined,
  round: undefined,
  dashed: undefined,
  autoInsertSpace: undefined,
  tag: 'button',
})

const resolvedIcon = computed(() => resolveIconComponent(props.icon))
const resolvedLoadingIcon = computed(() => resolveIconComponent(props.loadingIcon))

const emit = defineEmits(buttonEmits)

const buttonStyle = useButtonCustomStyle(props)
const ns = useNamespace('button')
const {
  _ref,
  _size,
  _type,
  _disabled,
  _props,
  _plain,
  _round,
  _text,
  _dashed,
  shouldAddSpace,
  handleClick,
} = useButton(props, emit)
const buttonKls = computed(() => [
  ns.b(),
  ns.m(_type.value),
  ns.m(_size.value),
  ns.is('disabled', _disabled.value),
  ns.is('loading', props.loading),
  ns.is('plain', _plain.value),
  ns.is('round', _round.value),
  ns.is('circle', props.circle),
  ns.is('text', _text.value),
  ns.is('dashed', _dashed.value),
  ns.is('link', props.link),
  ns.is('has-bg', props.bg),
])

defineExpose({
  /** @description button html element */
  ref: _ref,
  /** @description button size */
  size: _size,
  /** @description button type */
  type: _type,
  /** @description button disabled */
  disabled: _disabled,
  /** @description whether adding space */
  shouldAddSpace,
})
</script>
